import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { Camera, ZoomIn, ZoomOut, Check, X, Loader2 } from 'lucide-react';
import { getCroppedImageBlob } from '../../lib/cropImage';
import { supabase, getStorageImageUrl } from '../../lib/supabase';

interface AvatarUploadProps {
  currentAvatarUrl?: string;
  userId: string;
  onAvatarUpdated: (newUrl: string) => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatarUrl,
  userId,
  onAvatarUpdated,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Abort in-flight upload on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    if (file.size > MAX_FILE_SIZE) {
      setError('Imagem deve ter no maximo 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Selecione um arquivo de imagem');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setIsCropping(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);

    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleCropConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setIsUploading(true);
    setError(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels);

      if (controller.signal.aborted) return;

      const filePath = `${userId}/avatar.webp`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, blob, {
          contentType: 'image/webp',
          upsert: true,
        });

      if (controller.signal.aborted) return;

      if (uploadError) {
        setError(uploadError.message);
        return;
      }

      const publicUrl = getStorageImageUrl(filePath);
      onAvatarUpdated(`${publicUrl}?t=${Date.now()}`);
      setIsCropping(false);
      setImageSrc(null);
    } catch (err) {
      if (!controller.signal.aborted) {
        setError('Erro ao fazer upload da imagem');
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsUploading(false);
      }
      abortRef.current = null;
    }
  };

  const handleCropCancel = () => {
    setIsCropping(false);
    setImageSrc(null);
    setError(null);
  };

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Avatar Preview */}
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="relative group"
        disabled={isUploading}
      >
        {currentAvatarUrl ? (
          <img
            src={currentAvatarUrl}
            alt="Avatar"
            className="w-24 h-24 rounded-full object-cover border-2 border-neutral-700"
          />
        ) : (
          <div className="w-24 h-24 rounded-full bg-neutral-800 border-2 border-neutral-700 flex items-center justify-center">
            <Camera className="w-8 h-8 text-neutral-500" />
          </div>
        )}
        <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Camera className="w-6 h-6 text-white" />
        </div>
      </button>

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
        disabled={isUploading}
      >
        Alterar foto
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {error && (
        <p className="text-sm text-red-400">{error}</p>
      )}

      {/* Crop Modal — rendered via Portal to escape parent stacking contexts */}
      {isCropping && imageSrc && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-neutral-900 rounded-2xl border border-neutral-700 w-full max-w-md mx-4 overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-700">
              <h3 className="text-white font-medium">Ajustar foto</h3>
              <button
                type="button"
                onClick={handleCropCancel}
                className="text-neutral-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Cropper Area */}
            <div className="relative w-full" style={{ height: 320 }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-3 px-6 py-3 border-t border-neutral-800">
              <ZoomOut className="w-4 h-4 text-neutral-400 flex-shrink-0" />
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 accent-primary-500"
              />
              <ZoomIn className="w-4 h-4 text-neutral-400 flex-shrink-0" />
            </div>

            {/* Actions */}
            <div className="flex gap-3 px-4 py-3 border-t border-neutral-700">
              <button
                type="button"
                onClick={handleCropCancel}
                className="flex-1 px-4 py-2.5 rounded-xl border border-neutral-600 text-neutral-300 hover:bg-neutral-800 transition-colors text-sm font-medium"
                disabled={isUploading}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCropConfirm}
                disabled={isUploading}
                className="flex-1 px-4 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-400 text-white transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Confirmar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
